import { Component, OnInit, Optional } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Category, Fascicle, PageTemplate } from '@nousmedis/cc-types';
import { TranslateService } from '@ngx-translate/core';
import { TreeNode } from 'primeng/api';
import { CatalogService } from '../../../services/catalog.service';
import { ProductCategoriesService } from '../../../services/product-categories.service';
import { ProductTagsService } from '../../../services/product-tags.service';
import { ProductService } from '../../../services/product.service';
import { FascicleService } from '../../../services/fascicle.service';
import { PageTemplateService } from 'app/modules/catalog-cloud/services/page-template.service';
import { fascicleFormFields } from '../../../schemas/fascicle-form.schema';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UserService } from '../../../../user/services/user.service';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-fascicle-form',
  templateUrl: './fascicle-form.component.html',
  styleUrls: ['./fascicle-form.component.scss'],
})
export class FascicleFormComponent implements OnInit {
  form = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];

  loadedForm = false;
  spinnerSave = false;
  searchingProducts = false;
  totalProducts = '0';

  categoryTreeNodes: TreeNode[] = [];
  selectedCategoryNodes: TreeNode[] = [];
  tagOptions: { label: string; value: string }[] = [];
  templateOptions: { label: string; value: string }[] = [];

  id = '';
  private idCatalog = '';

  constructor(
    private catalogService: CatalogService,
    private fascicleService: FascicleService,
    private categoriesService: ProductCategoriesService,
    private tagsService: ProductTagsService,
    private productService: ProductService,
    private pageTemplateService: PageTemplateService,
    private userService: UserService,
    private messageService: MessageService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    @Optional() private dialogConfig?: DynamicDialogConfig,
    @Optional() public dialogRef?: DynamicDialogRef,
  ) {}

  async ngOnInit(): Promise<void> {
    this.id = this.dialogConfig?.data?.id ?? this.route.snapshot.paramMap.get('id') ?? '';
    this.idCatalog = this.dialogConfig?.data?.idCatalog ?? this.route.snapshot.paramMap.get('idCatalog') ?? '';

    const [templates, tags, categories] = await Promise.all([
      firstValueFrom(this.pageTemplateService.getTemplates()),
      firstValueFrom(this.tagsService.getByCatalog(this.idCatalog)),
      firstValueFrom(this.categoriesService.getByCatalog(this.idCatalog)),
    ]);

    this.categoryTreeNodes = this.buildCategoryTree(categories);

    this.tagOptions = tags.map((t: string) => ({ label: t, value: t }));

    this.templateOptions = templates.map((t: PageTemplate) => ({
      label: t.name,
      value: t.id,
    }));

    this.fields = fascicleFormFields(this.translate, this.templateOptions);

    if (this.id) {
      const fascicle: Fascicle = await firstValueFrom(
        this.fascicleService.get(this.idCatalog, this.id)
      );
      this.model = {
        ...fascicle,
        idCatalog: this.idCatalog,
        defaultTemplate: fascicle.defaultTemplate
          ? (fascicle.defaultTemplate as any).id ?? fascicle.defaultTemplate
          : null,
      };
      this.selectedCategoryNodes = this.getSelectedNodes(this.categoryTreeNodes, this.model.categories || []);
    } else {
      const userId = this.userService.getCurrentUser().id;
      const fascicle = this.fascicleService.initialize(userId);
      this.model = {
        ...fascicle,
        idCatalog: this.idCatalog,
        updatedBy: userId,
        updatedAt: new Date().toISOString(),
        categories: [],
        tags: [],
      };
    }

    this.loadedForm = true;
    this.countFilteredProducts();

    this.form.valueChanges.subscribe(() => {
      // form validity handled via form.valid in template
    });
  }

  onCategoriesChange(): void {
    this.model.categories = this.selectedCategoryNodes.map(n => n.key!);
    this.countFilteredProducts();
  }

  private buildCategoryTree(categories: Category[]): TreeNode[] {
    const map = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    for (const c of categories) {
      map.set(c.externalId, { key: c.id, label: (c as any).composedName || c.name, children: [] });
    }

    for (const c of categories) {
      const node = map.get(c.externalId)!;
      if (c.idParent && map.has(c.idParent)) {
        map.get(c.idParent)!.children!.push(node);
      } else {
        roots.push(node);
      }
    }

    return roots;
  }

  private getSelectedNodes(nodes: TreeNode[], ids: string[]): TreeNode[] {
    const result: TreeNode[] = [];
    const walk = (list: TreeNode[]) => {
      for (const n of list) {
        if (ids.includes(n.key!)) result.push(n);
        if (n.children?.length) walk(n.children);
      }
    };
    walk(nodes);
    return result;
  }

  onTagsChange(): void {
    this.countFilteredProducts();
  }

  countFilteredProducts(): void {
    this.searchingProducts = true;
    this.productService
      .countProducts(this.idCatalog, this.model.categories || [], this.model.tags || [])
      .subscribe((data) => {
        this.totalProducts = data;
        this.searchingProducts = false;
      });
  }

  save(): void {
    this.spinnerSave = true;
    this.form.disable();

    this.fascicleService.saveProperties(this.model).subscribe(
      (result: any) => {
        this.catalogService.fetchCatalogList();
        this.messageService.add({
          severity: 'info',
          summary: this.model.id ? 'Fascículo actualizado' : 'Fascículo creado',
          detail: 'Guardado de fasciculo',
          key: 'root-msg',
        });
        this.spinnerSave = false;
        this.form.enable();

        if (this.dialogRef) {
          this.dialogRef.close(result);
        } else {
          this.router.navigate(['/catalog', this.idCatalog, 'fascicle', result?.newFascicleId]);
        }
      },
      () => {
        this.spinnerSave = false;
        this.form.enable();
      }
    );
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogConfig!.data.cancel = true;
      this.dialogRef.close();
    } else {
      this.router.navigate(['/catalog', this.idCatalog, 'fascicles']);
    }
  }
}
